/** Global tracking event key, not necessarily tied to any tracker */
export type TrackingEventKey =
  | "updated_business_content"
  | "created_business"
  | "upgraded_wl_tier"
  | "invited_team_member"
  | "invited_client"
  | "added_memory"
  | "generated_posts"
  | "used_post_editor"
  | "updated_topics"
  | "created_custom_post"
  | "user_created"
  | "added_social"
  | "generator_panel"
  | "new_post_decided"
  | "sign_up"
  | "tutorial_complete"
  | "view_item_list"
  | "begin_checkout"
  | "purchase"
  | "user_data"
  | "experiment_viewed"
  | "scheduled_post"
  | "liked_post"
  | "deleted_post"
  | "published_post"
  | "added_images"
  | "added_comment"
  | "marked_post_ready_for_review"
  | "attempted_calendar_event_drag"
  | "topics_category_created"
  | "topics_category_updated";

const DEFAULT_EVENT_MAPPING = {
  fbqEvent: undefined,
  gtmEvent: undefined,
  ttqEvent: undefined,
};

// RAW_EVENT_MAPPING_DICT is an extension of type EventMapping, but it needs to be interpretted literally
// to enforce params in trackEvent. Do not type as EventMapping.
const RAW_EVENT_MAPPING_DICT = {
  created_business: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "created_business",
  },
  upgraded_wl_tier: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "upgraded_wl_tier",
  },
  created_custom_post: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "created_custom_post",
  },
  added_images: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "added_images",
  },
  invited_team_member: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "invited_team_member",
  },
  invited_client: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "invited_client",
  },
  used_post_editor: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "used_post_editor",
  },
  updated_topics: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "updated_topics",
  },
  updated_business_content: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "updated_business_content",
  },
  user_created: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "user_created",
  },
  added_social: { ...DEFAULT_EVENT_MAPPING, gtmEvent: "added_social" },
  added_memory: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "added_memory",
  },
  generated_posts: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "generated_posts",
  },
  generator_panel: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "generator_panel",
  },
  new_post_decided: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "new_post_decided",
  },
  sign_up: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "sign_up",
    ttqEvent: "CompleteRegistration",
  },
  tutorial_complete: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "tutorial_complete",
  },
  view_item_list: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "view_item_list",
  },
  begin_checkout: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "begin_checkout",
  },
  purchase: {
    ...DEFAULT_EVENT_MAPPING,
    fbqEvent: "Purchase",
    gtmEvent: "purchase",
    ttqEvent: "CompletePayment",
  },
  user_data: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "userData",
  },
  experiment_viewed: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "experiment_viewed",
  },
  scheduled_post: {
    ...DEFAULT_EVENT_MAPPING,
  },
  liked_post: {
    ...DEFAULT_EVENT_MAPPING,
  },
  deleted_post: {
    ...DEFAULT_EVENT_MAPPING,
  },
  published_post: {
    ...DEFAULT_EVENT_MAPPING,
  },
  added_comment: {
    ...DEFAULT_EVENT_MAPPING,
  },
  marked_post_ready_for_review: {
    ...DEFAULT_EVENT_MAPPING,
  },
  attempted_calendar_event_drag: {
    ...DEFAULT_EVENT_MAPPING,
    gtmEvent: "attempted_calendar_event_drag",
  },
} as const;

// Add mappings for the new events (assuming no specific GTM/FBQ/TTQ/Refiner for now)
const NEW_EVENT_MAPPINGS = {
  topics_category_created: {
    ...DEFAULT_EVENT_MAPPING,
    // Add specific service mappings here if needed in the future
  },
  topics_category_updated: {
    ...DEFAULT_EVENT_MAPPING,
    // Add specific service mappings here if needed in the future
  },
};

export type EventMapping = typeof RAW_EVENT_MAPPING_DICT;

/**
 * Shorthand event keys:
 * fbqEvent - Facebook Pixel
 * gtmEvent - Google Tag Manager
 * ttqEvent - TikTok Pixel
 */
type GenericEventMapping = {
  [key in TrackingEventKey]: {
    fbqEvent: MetaPixelEvent | undefined;
    gtmEvent: GtmEvent | undefined;
    ttqEvent: TikTokEvent | undefined;
  };
};

// This type conversion will error out if RAW_EVENT_MAPPING_DICT violates some constraints of EventMapping
export const EVENT_MAPPING_DICT = {
  ...RAW_EVENT_MAPPING_DICT,
  ...NEW_EVENT_MAPPINGS,
} as GenericEventMapping;
